import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { webhookIntegrationsService } from '../../../services/webhookIntegrationsService';

const WebhooksTab = ({ formId, formTitle }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    webhookName: '',
    webhookUrl: '',
    enabled: true
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    loadWebhooks();
  }, [formId]);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await webhookIntegrationsService?.getWebhookIntegrations(formId);
      setWebhooks(data || []);
    } catch (err) {
      console.error('Error loading webhooks:', err);
      setError(err?.message || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWebhook = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      if (!newWebhook?.webhookName?.trim()) {
        setError('Please enter a webhook name');
        return;
      }

      if (!newWebhook?.webhookUrl?.trim()) {
        setError('Please enter a webhook URL');
        return;
      }

      // Basic URL validation
      try {
        new URL(newWebhook?.webhookUrl);
      } catch {
        setError('Please enter a valid URL');
        return;
      }

      await webhookIntegrationsService?.createWebhookIntegration(formId, newWebhook);
      setSuccessMessage('Webhook added successfully!');
      setShowAddForm(false);
      setNewWebhook({ webhookName: '', webhookUrl: '', enabled: true });
      await loadWebhooks();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error adding webhook:', err);
      setError(err?.message || 'Failed to add webhook');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleWebhook = async (webhookId, currentEnabled) => {
    try {
      setError(null);
      await webhookIntegrationsService?.updateWebhookIntegration(webhookId, {
        enabled: !currentEnabled
      });
      await loadWebhooks();
    } catch (err) {
      console.error('Error toggling webhook:', err);
      setError(err?.message || 'Failed to toggle webhook');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;

    try {
      setError(null);
      await webhookIntegrationsService?.deleteWebhookIntegration(webhookId);
      setSuccessMessage('Webhook deleted successfully!');
      await loadWebhooks();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting webhook:', err);
      setError(err?.message || 'Failed to delete webhook');
    }
  };

  const handleTestWebhook = async (webhookId) => {
    try {
      setTesting(webhookId);
      setError(null);
      setSuccessMessage(null);

      const testData = {
        name: 'Test User',
        email: 'test@example.com',
        message: 'This is a test submission from WebForms.biz'
      };

      await webhookIntegrationsService?.testWebhook(webhookId, testData);
      setSuccessMessage('Test webhook sent successfully! Check your Zapier dashboard.');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error testing webhook:', err);
      setError(err?.message || 'Failed to send test webhook');
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
            Zapier Webhooks
          </h3>
          <p className="text-sm text-muted-foreground">
            Send form submissions to Zapier to trigger workflows in 5000+ apps including CRMs, Slack, Google Sheets, and more.
          </p>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/20 rounded-lg">
          <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
          <p className="text-sm text-success">{successMessage}</p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-sm text-foreground font-medium">
              How to connect Zapier:
            </p>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Create a new Zap in your Zapier account</li>
              <li>Choose "Webhooks by Zapier" as the trigger app</li>
              <li>Select "Catch Hook" as the trigger event</li>
              <li>Copy the webhook URL provided by Zapier</li>
              <li>Add the webhook URL below and test it</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Webhooks List */}
      {webhooks?.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-foreground">Active Webhooks</h4>
          {webhooks?.map((webhook) => (
            <div
              key={webhook?.id}
              className="bg-muted/50 rounded-lg p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h5 className="text-sm font-medium text-foreground">
                      {webhook?.webhookName}
                    </h5>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded ${
                        webhook?.enabled
                          ? 'bg-success/10 text-success' :'bg-muted-foreground/10 text-muted-foreground'
                      }`}
                    >
                      {webhook?.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground break-all">
                    {webhook?.webhookUrl}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleWebhook(webhook?.id, webhook?.enabled)}
                  iconName={webhook?.enabled ? 'ToggleRight' : 'ToggleLeft'}
                  iconPosition="left"
                >
                  {webhook?.enabled ? 'Disable' : 'Enable'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTestWebhook(webhook?.id)}
                  disabled={testing === webhook?.id || !webhook?.enabled}
                  iconName={testing === webhook?.id ? 'Loader2' : 'Send'}
                  iconPosition="left"
                >
                  {testing === webhook?.id ? 'Sending...' : 'Test'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteWebhook(webhook?.id)}
                  iconName="Trash2"
                  className="text-destructive hover:text-destructive"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Webhook Form */}
      {showAddForm ? (
        <div className="bg-muted/50 rounded-lg p-4 space-y-4">
          <h4 className="text-sm font-medium text-foreground">Add New Webhook</h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Webhook Name
              </label>
              <Input
                type="text"
                placeholder="e.g., My Zapier Workflow"
                value={newWebhook?.webhookName}
                onChange={(e) => setNewWebhook({ ...newWebhook, webhookName: e?.target?.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Webhook URL
              </label>
              <Input
                type="url"
                placeholder="https://hooks.zapier.com/hooks/catch/..."
                value={newWebhook?.webhookUrl}
                onChange={(e) => setNewWebhook({ ...newWebhook, webhookUrl: e?.target?.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Paste the webhook URL from your Zapier Zap
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="webhook-enabled"
                checked={newWebhook?.enabled}
                onCheckedChange={(checked) => setNewWebhook({ ...newWebhook, enabled: checked })}
              />
              <label htmlFor="webhook-enabled" className="text-sm text-foreground cursor-pointer">
                Enable webhook immediately
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="default"
              onClick={handleAddWebhook}
              disabled={saving}
              iconName={saving ? 'Loader2' : 'Plus'}
              iconPosition="left"
            >
              {saving ? 'Adding...' : 'Add Webhook'}
            </Button>
            <Button
              variant="outline"
              size="default"
              onClick={() => {
                setShowAddForm(false);
                setNewWebhook({ webhookName: '', webhookUrl: '', enabled: true });
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="default"
          size="default"
          onClick={() => setShowAddForm(true)}
          iconName="Plus"
          iconPosition="left"
        >
          Add Webhook
        </Button>
      )}

      {/* Empty State */}
      {webhooks?.length === 0 && !showAddForm && (
        <div className="text-center py-12 bg-muted/30 rounded-lg border-2 border-dashed border-border">
          <Icon name="Webhook" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h4 className="text-base font-medium text-foreground mb-2">
            No webhooks configured
          </h4>
          <p className="text-sm text-muted-foreground mb-4">
            Add a webhook to start sending form submissions to Zapier
          </p>
        </div>
      )}
    </div>
  );
};

export default WebhooksTab;